/**
 * Die **Markerstriche je Zeile** im Projekttitel (`.claude/tasks/title-marker-line-wrap.md`).
 *
 * Bricht ein markierter Titel um, ist **jede Zeile ein eigener Markerstrich**
 * — dieselbe Regel wie auf der Pinnwand (CONTEXT.md, Text → Hervorheben). Aus
 * den Rechtecken der Marker-Stücke (ein Rechteck je Zeile, `getClientRects`)
 * wird hier je Markerstrich und Zeile EIN Strich: Stücke desselben Strichs auf
 * derselben Zeile (ein fettes Wort darin) liegen zusammen.
 *
 * Reine Rechnung ohne DOM, damit sie ohne Layout zu prüfen ist.
 */

export interface TitleMarkerRect {
  left: number
  right: number
  top: number
  bottom: number
}

export interface TitleMarkerPieceRects {
  /** Die Nummer des Markerstrichs im Titel (`data-marker-stroke`). */
  stroke: number
  /** Die Saat seiner Form (`data-marker-seed`). */
  seed: number
  /** Ein Rechteck je Zeile, auf der das Stück steht. */
  rects: readonly TitleMarkerRect[]
}

export interface TitleMarkerLine extends TitleMarkerRect {
  stroke: number
  /** Die Saat dieser Zeile: die erste Zeile behält die des Strichs, jede weitere bekommt eine eigene. */
  seed: number
  /** Die wievielte Zeile des Markerstrichs, ab 0. */
  lineOfStroke: number
}

/** Ein Rechteck gehört zur Zeile, wenn seine Mitte in ihrer oberen oder unteren Hälfte liegt. */
function sameLine(a: TitleMarkerRect, b: TitleMarkerRect): boolean {
  const centerA = (a.top + a.bottom) / 2
  const centerB = (b.top + b.bottom) / 2
  const half = Math.min(a.bottom - a.top, b.bottom - b.top) / 2
  return Math.abs(centerA - centerB) < half
}

/** Eine andere, aber feste Saat je weiterer Zeile. */
export function titleMarkerLineSeed(seed: number, lineOfStroke: number): number {
  return lineOfStroke === 0 ? seed : (seed + Math.imul(lineOfStroke, 0x9e3779b1)) >>> 0
}

/**
 * Die Striche je Zeile, in Lesereihenfolge.
 *
 * @example
 * titleMarkerLines([{ stroke: 0, seed: 7, rects: [lineOne, lineTwo] }])
 * // → zwei Striche: { stroke: 0, lineOfStroke: 0, … }, { stroke: 0, lineOfStroke: 1, … }
 */
export function titleMarkerLines(pieces: readonly TitleMarkerPieceRects[]): TitleMarkerLine[] {
  // Leere Rechtecke (an der Umbruchstelle meldet der Browser manchmal eines) zählen nicht.
  const entries = pieces.flatMap(piece => piece.rects
    .filter(rect => rect.right - rect.left > 0.5 && rect.bottom - rect.top > 0.5)
    .map(rect => ({ stroke: piece.stroke, seed: piece.seed, rect })))

  // Zeilen: von oben nach unten, jedes Rechteck in die erste Zeile, zu der es passt.
  const lines: TitleMarkerRect[] = []
  const lineOf = entries.map(({ rect }) => {
    const index = lines.findIndex(line => sameLine(line, rect))
    if (index >= 0) return index
    lines.push(rect)
    return lines.length - 1
  })
  const order = lines.map((line, index) => ({ index, top: line.top })).sort((a, b) => a.top - b.top)
  const rank = new Map(order.map((line, position) => [line.index, position]))

  // Je Markerstrich und Zeile ein Strich über alle seine Stücke dort.
  const byKey = new Map<string, { stroke: number; seed: number; line: number; rect: TitleMarkerRect }>()
  entries.forEach(({ stroke, seed, rect }, i) => {
    const line = rank.get(lineOf[i]!)!
    const key = `${stroke}:${line}`
    const found = byKey.get(key)
    if (!found) {
      byKey.set(key, { stroke, seed, line, rect: { ...rect } })
      return
    }
    found.rect = {
      left: Math.min(found.rect.left, rect.left),
      right: Math.max(found.rect.right, rect.right),
      top: Math.min(found.rect.top, rect.top),
      bottom: Math.max(found.rect.bottom, rect.bottom),
    }
  })

  const segments = [...byKey.values()].sort((a, b) => a.line - b.line || a.rect.left - b.rect.left)
  const seen = new Map<number, number>()
  return segments.map(({ stroke, seed, rect }) => {
    const lineOfStroke = seen.get(stroke) ?? 0
    seen.set(stroke, lineOfStroke + 1)
    return { ...rect, stroke, seed: titleMarkerLineSeed(seed, lineOfStroke), lineOfStroke }
  })
}
